import React from 'react';
import { Check } from 'lucide-react';

interface CRMPricingProps {
  onStartTrial: () => void;
}

export default function CRMPricing({ onStartTrial }: CRMPricingProps) {
  const plans = [
    {
      name: 'Starter',
      price: '$49',
      description: 'Perfect for small businesses getting started',
      features: [
        'Up to 1,000 contacts',
        'Email marketing',
        'Basic automation',
        'Landing page builder',
        'Mobile app access'
      ]
    },
    {
      name: 'Professional',
      price: '$99',
      description: 'For growing businesses needing more power',
      features: [
        'Up to 10,000 contacts',
        'Advanced automation',
        'Sales pipeline',
        'Team collaboration',
        'API access',
        'Priority support'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      description: 'For large organizations with custom needs',
      features: [
        'Unlimited contacts',
        'Custom integrations',
        'Dedicated support',
        'White labeling',
        'Custom training',
        'SLA guarantee'
      ]
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl mb-4">
          Simple, Transparent Pricing
        </h2>
        <p className="mt-4 text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          Choose the plan that's right for your business
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden ${
              plan.popular ? 'ring-2 ring-purple-600' : ''
            }`}
          >
            {plan.popular && (
              <div className="absolute top-0 right-0 bg-purple-600 text-white px-4 py-1 text-sm">
                Popular
              </div>
            )}
            
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {plan.name}
              </h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">
                  {plan.price}
                </span>
                <span className="text-gray-600 dark:text-gray-300">/month</span>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-8">
                {plan.description}
              </p>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <Check className="h-5 w-5 text-purple-600 flex-shrink-0" />
                    <span className="ml-3 text-gray-600 dark:text-gray-300">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={onStartTrial}
                className={`w-full text-center px-6 py-3 rounded-md text-base font-medium transition-colors ${
                  plan.popular
                    ? 'text-white bg-purple-600 hover:bg-purple-700'
                    : 'text-purple-600 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800'
                }`}
              >
                Get Started
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}